import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { CalendarEventFilters, CalendarViewMode } from '@/modules/calendar/types';
import {
  addDays,
  addMonths,
  getDefaultAnchorDate,
  getVisibleRange,
  parseCalendarSearchParams,
} from '@/modules/calendar/utils';

export function useReservationCalendar() {
  const [searchParams, setSearchParams] = useSearchParams();

  const parsed = useMemo(
    () => parseCalendarSearchParams(searchParams),
    [searchParams],
  );

  const view: CalendarViewMode = parsed.view ?? 'month';
  const anchorDate = parsed.date ?? getDefaultAnchorDate();
  const filters: CalendarEventFilters = useMemo(
    () => ({
      showReservations: true,
      showLogistics: true,
      ...(parsed.status ? { status: parsed.status } : {}),
      ...(parsed.itemId ? { itemId: parsed.itemId } : {}),
      ...(parsed.categoryId ? { categoryId: parsed.categoryId } : {}),
    }),
    [parsed.status, parsed.itemId, parsed.categoryId],
  );

  const visibleRange = useMemo(
    () => getVisibleRange(view, anchorDate),
    [view, anchorDate],
  );

  const updateParams = useCallback(
    (patch: Record<string, string | null>) => {
      const next = new URLSearchParams(searchParams);
      for (const [key, value] of Object.entries(patch)) {
        if (value === null || value === '') next.delete(key);
        else next.set(key, value);
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const setView = useCallback(
    (nextView: CalendarViewMode) => updateParams({ view: nextView }),
    [updateParams],
  );

  const setAnchorDate = useCallback(
    (iso: string) => updateParams({ date: iso }),
    [updateParams],
  );

  const setFilters = useCallback(
    (next: CalendarEventFilters) => {
      updateParams({
        status: next.status && next.status !== 'all' ? next.status : null,
        item: next.itemId ?? null,
        category: next.categoryId ?? null,
      });
    },
    [updateParams],
  );

  const navigatePrev = useCallback(() => {
    const next =
      view === 'month'
        ? addMonths(anchorDate, -1)
        : view === 'week'
          ? addDays(anchorDate, -7)
          : addDays(anchorDate, -1);
    setAnchorDate(next);
  }, [view, anchorDate, setAnchorDate]);

  const navigateNext = useCallback(() => {
    const next =
      view === 'month'
        ? addMonths(anchorDate, 1)
        : view === 'week'
          ? addDays(anchorDate, 7)
          : addDays(anchorDate, 1);
    setAnchorDate(next);
  }, [view, anchorDate, setAnchorDate]);

  const goToToday = useCallback(() => {
    setAnchorDate(getDefaultAnchorDate());
  }, [setAnchorDate]);

  return {
    view,
    anchorDate,
    filters,
    visibleRange,
    setView,
    setAnchorDate,
    setFilters,
    navigatePrev,
    navigateNext,
    goToToday,
  };
}
